import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Dealer component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconDealer extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.06738 3.10878C3.06738 2.58131 3.4509 2.2522 3.807 2.2522H14.304V10.3917H12.0102L12 10.3916L11.9898 10.3917H3.807C3.4509 10.3917 3.06738 10.0626 3.06738 9.53509V3.10878ZM20.1928 0.752197H15.054H3.807C2.51772 0.752197 1.56738 1.86166 1.56738 3.10878V9.53509C1.56738 10.7822 2.51772 11.8917 3.807 11.8917H11.25V21.6316H4.55176C4.13754 21.6316 3.80176 21.9674 3.80176 22.3816C3.80176 22.7958 4.13754 23.1316 4.55176 23.1316H11.9718C11.9812 23.1319 11.9906 23.1321 12 23.1321C12.0094 23.1321 12.0188 23.1319 12.0282 23.1316H19.448C19.8622 23.1316 20.198 22.7958 20.198 22.3816C20.198 21.9674 19.8622 21.6316 19.448 21.6316H12.75V11.8917H20.1928C21.4821 11.8917 22.4325 10.7822 22.4325 9.53509V3.10878C22.4325 1.86166 21.4821 0.752197 20.1928 0.752197ZM15.804 10.3917V2.2522H20.1928C20.549 2.2522 20.9325 2.58131 20.9325 3.10878V9.53509C20.9325 10.0626 20.549 10.3917 20.1928 10.3917H15.804Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-dealer') === undefined) {
  customElements.define('cat-icon-dealer', BlocksIconDealer);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-dealer': BlocksIconDealer;
  }
}
